import type { stacks } from "@dropbox/api-v2-client";
import { ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { useCurrentBackgroundTheme } from "@mirage/stacks/themes";
import { canEditStack, getStackBannerUrl } from "@mirage/stacks/utils";
import classNames from "classnames";
import { useRef, useState } from "react";
import { useShareModal } from "../ShareModal/ShareModal";
import { stackHasWritePermissions } from "../utils/helpers";
import styles from "./Header.module.css";
import { StackBannerEditor } from "./StackBannerEditor";
import { StackDescriptionEditor } from "./StackDescriptionEditor";
import { StackIconEditor } from "./StackIconEditor";
import { StackNameEditor } from "./StackNameEditor";

export const Header = ({
  initialCanEdit,
  mutationRequestId,
  sessionId,
  stack,
}: {
  initialCanEdit: boolean;
  mutationRequestId?: string;
  sessionId?: string;
  stack: stacks.Stack | null;
}) => {
  const backgroundTheme = useCurrentBackgroundTheme(stack);
  const { shareModalJsx } = useShareModal({ stack });
  const headerContainerRef = useRef(null);
  const [isEditingStackName, setIsEditingStackName] = useState(false);

  const canEdit = initialCanEdit ?? canEditStack(stack);
  const bannerUrl = getStackBannerUrl(stack);
  const hasEditPermission = stack && stackHasWritePermissions(stack);

  return (
    <ThemeProvider overrides={backgroundTheme}>
      <ThemeContainer>
        <div
          className={classNames(
            styles.headerContainer,
            bannerUrl && styles.headerContainerWithBanner,
          )}
          ref={headerContainerRef}
          style={
            {
              "--stack-banner-url": bannerUrl ? `url(${bannerUrl})` : "none",
            } as React.CSSProperties
          }
        >
          {hasEditPermission && <StackBannerEditor stack={stack} />}
          <StackIconEditor
            canEdit={canEdit}
            className={styles.stackIconEditor}
            mutationRequestId={mutationRequestId}
            sessionId={sessionId}
            stack={stack}
          />
        </div>
        <div className={styles.headerMainRow}>
          <div>
            <StackNameEditor
              canEdit={canEdit}
              editing={isEditingStackName}
              mutationRequestId={mutationRequestId}
              onEditStatusUpdate={setIsEditingStackName}
              sessionId={sessionId}
              stack={stack}
            />
            <StackDescriptionEditor
              canEdit={canEdit}
              editing={isEditingStackName}
              mutationRequestId={mutationRequestId}
              onEditStatusUpdate={setIsEditingStackName}
              sessionId={sessionId}
              stack={stack}
            />
          </div>
        </div>
        {shareModalJsx}
      </ThemeContainer>
    </ThemeProvider>
  );
};
